import { useState, type ReactNode } from "react";
import { reviewTemplate } from "../../shared/review-templates";
import type { ActionCall, ApprovalDTO, ReviewDecision } from "../../shared/types";
import { ApiError, get, post } from "../lib/api";
import { releaseState, verdictWord, waitingForText } from "../lib/approval-text";
import { Button, ErrorText, inputCls, timeAgo } from "./ui";
import { NoteMic } from "./VoiceNote";

/**
 * One verdict set for every kind of request but a question (D14): Approve, Reject, or Request revision with the words
 * that say what to change. The cards, the Requests page and the review pages all draw it from here.
 */

/** "taken back by the agent" as a line of its own: a capital, and a full stop unless it has one. */
const sentence = (s: string) => `${s.charAt(0).toUpperCase()}${s.slice(1)}${/[.!?]$/.test(s) ? "" : "."}`;

/** The tool an approved item is sent through, by name, for the line that says to check it. */
const toolOf = (a: ApprovalDTO) => {
  if (a.kind === "action") return (a.payload as Partial<ActionCall>).connectorName ?? "the tool";
  const release = a.template ? reviewTemplate(a.template)?.release : null;
  return release === "salesos-outreach" ? "Outreach" : release === "slack-user" ? "Slack" : "the tool";
};

/**
 * Approve, Reject, or Request revision with a note. One click decides; a second is refused. A card may name the third
 * verdict its own way ("Request changes") and offer the mic beside the note.
 */
export function VerdictBar({ a, onDecided, reviseLabel = "Request revision", voice = false }: {
  a: ApprovalDTO; onDecided: (updated?: ApprovalDTO) => void; reviseLabel?: string; voice?: boolean;
}) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // An access request is a yes or no (AIT-105): no revision, and a no comes with its reason, which the person is told.
  const yesOrNo = a.kind === "access";
  const decide = async (decision: ReviewDecision) => {
    if (decision === "revise" && !note.trim()) return setError("Say what to change: a revision needs a note.");
    if (yesOrNo && decision === "reject" && !note.trim()) return setError("Say why: the person is told the reason.");
    setBusy(true);
    setError(null);
    try {
      onDecided(await post<ApprovalDTO>(`/api/approvals/${a.id}/decide`, { decision, note: note.trim() || undefined }));
    } catch (err) {
      // Someone else decided it, or is deciding it right now: show where it actually stands instead of an error.
      const fresh = err instanceof ApiError && err.status === 409 ? await get<ApprovalDTO>(`/api/approvals/${a.id}`).catch(() => null) : null;
      if (fresh && fresh.status !== "pending") return onDecided(fresh);
      setError((err as Error).message);
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <textarea
        className={inputCls}
        rows={2}
        placeholder={yesOrNo ? "Why not — required to reject" : `What to change, or why not — required to ${reviseLabel.toLowerCase()}`}
        value={note}
        disabled={busy}
        onChange={(e) => setNote(e.target.value)}
      />
      {voice && <NoteMic disabled={busy} onBusy={setRecording} onText={(spoken) => setNote((current) => (current.trim() ? `${current.trim()} ${spoken}` : spoken))} />}
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" disabled={busy || recording} onClick={() => decide("approve")}>
          Approve
        </Button>
        {!yesOrNo && (
          <Button disabled={busy || recording} onClick={() => decide("revise")}>
            {reviseLabel}
          </Button>
        )}
        <Button variant="danger" disabled={busy || recording} onClick={() => decide("reject")}>
          Reject
        </Button>
      </div>
      <ErrorText error={error} />
    </div>
  );
}

/** Who decides instead, in one line: the person it is addressed to, or the agent's owners. */
export function AddressedTo({ a, suffix = "" }: { a: ApprovalDTO; suffix?: string }) {
  return (
    <p className="text-sm text-muted">
      Addressed to {waitingForText(a)}
      {suffix}.{!a.ownerUserId && !a.anyAdmin && " An admin who needs to decide it becomes an owner of the agent."}
    </p>
  );
}

/** The three verdicts, drawn but off, for anyone it is not addressed to: an admin reading somebody else's leaves it alone. */
export function LockedVerdicts({ a, reviseLabel = "Request revision" }: { a: ApprovalDTO; reviseLabel?: string }) {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2">
        <Button variant="primary" disabled>
          Approve
        </Button>
        {a.kind !== "access" && <Button disabled>{reviseLabel}</Button>}
        <Button variant="danger" disabled>
          Reject
        </Button>
      </div>
      <AddressedTo a={a} />
    </div>
  );
}

/**
 * What happened, once somebody decided: the verdict (an older answer's own label when the row carries one), who gave
 * it and when, whether an item the platform sends went, anything the card adds, and the words they left. A withdrawn
 * request says why it was taken back.
 */
export function Decided({ a, word, children }: { a: ApprovalDTO; word?: string; children?: ReactNode }) {
  const mark = a.status === "approved" ? "✓" : a.status === "revision_requested" ? "↻" : a.status === "withdrawn" ? "–" : "✕";
  const release = releaseState(a);
  return (
    <div className="mt-4 border-t border-line pt-3 text-sm">
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-muted">
        <span className={a.status === "approved" ? "text-success" : "text-faint"}>{mark}</span>
        <span className="font-medium text-fg">{word ?? verdictWord(a)}</span>
        <span>
          {/* A request the agent took back, or the platform closed, was decided by nobody. */}
          {a.decidedByName ? `by ${a.decidedByName} ` : a.status === "withdrawn" ? "" : "by someone "}
          {timeAgo(a.decidedAt)}
        </span>
        {/* An item the platform sends says whether it went, after who approved it: "approved by Dimi · not sent". */}
        {release && (
          <>
            <span className="text-faint">·</span>
            <span className={release === "sent" ? "text-success" : "text-warning"}>{release === "sent" ? "sent" : release === "unsent" ? "not sent" : "may have been sent"}</span>
          </>
        )}
        {children}
      </div>
      {a.decisionNote &&
        (a.status === "withdrawn" ? (
          <p className="mt-1.5 break-words text-muted">{sentence(a.decisionNote)}</p>
        ) : (
          <p className="mt-1.5 break-words whitespace-pre-wrap text-muted">“{a.decisionNote}”</p>
        ))}
      {a.error && <p className="mt-1.5 text-danger">Could not apply: {a.error}</p>}
    </div>
  );
}

/**
 * Send again and Withdraw, for an approved item the tool refused. A 409 means it moved meanwhile (it went, or was
 * withdrawn somewhere else), so the row is read again and shown as it stands.
 */
function useRelease(a: ApprovalDTO, onChanged: (updated: ApprovalDTO) => void) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const act = async (path: "send" | "withdraw" | "checked", body: { reason?: string } = {}) => {
    setBusy(true);
    setError(null);
    try {
      onChanged(await post<ApprovalDTO>(`/api/approvals/${a.id}/${path}`, body));
    } catch (err) {
      const fresh = err instanceof ApiError && err.status === 409 ? await get<ApprovalDTO>(`/api/approvals/${a.id}`).catch(() => null) : null;
      if (fresh) onChanged(fresh);
      setError((err as Error).message);
      throw err;
    } finally {
      setBusy(false);
    }
  };
  return {
    busy,
    error,
    sendAgain: () => act("send"),
    checked: () => act("checked"),
    withdraw: (reason?: string) => act("withdraw", { reason: reason?.trim() || undefined }),
  };
}

/** The line under an approved item that sends: its id in the tool, why it did not go, or that it may have — and who checked. */
function ReleaseNote({ a }: { a: ApprovalDTO }) {
  const state = releaseState(a);
  if (state === "sent") return a.usedRef ? <p className="mt-1 font-mono text-tiny break-all text-faint">{a.usedRef}</p> : null;
  if (state === "uncertain") {
    return a.releaseCheckedAt ? (
      <p className="mt-1.5 text-sm text-muted">May have been sent — checked in {toolOf(a)} by {a.releaseCheckedByName ?? "someone"} {timeAgo(a.releaseCheckedAt)}.</p>
    ) : (
      <p className="mt-2 rounded-xl bg-warning/10 px-3 py-2 text-sm text-warning">May have been sent — check {toolOf(a)} before anything else.</p>
    );
  }
  if (state === "unsent" && a.releaseError) return <p className="mt-1.5 break-words text-sm text-warning">Not sent: {a.releaseError}</p>;
  return null;
}

/**
 * An approved item that sends, on a card: what became of the send, and for its addressee Send again or Withdraw while
 * it has not gone. Withdrawing asks for a reason first (optional), since the item can never be sent after it. One that
 * may have gone offers "Checked in Outreach" to its addressee and the agent's owners, until one of them has.
 */
export function ReleaseOutcome({ a, canAct, canCheck = false, onChanged }: { a: ApprovalDTO; canAct: boolean; canCheck?: boolean; onChanged: (updated: ApprovalDTO) => void }) {
  const release = useRelease(a, onChanged);
  const [withdrawing, setWithdrawing] = useState(false);
  const [reason, setReason] = useState("");
  const state = releaseState(a);
  if (!state) return null;
  return (
    <>
      <ReleaseNote a={a} />
      {state === "uncertain" && !a.releaseCheckedAt && canCheck && (
        <div className="mt-3 space-y-2">
          <Button disabled={release.busy} onClick={() => release.checked().catch(() => {})}>
            Checked in {toolOf(a)}
          </Button>
          <ErrorText error={release.error} />
        </div>
      )}
      {state === "unsent" && canAct && (
        <div className="mt-3 space-y-2">
          {withdrawing && (
            <input className={inputCls} autoFocus placeholder="Why, for the agent (optional)" value={reason} disabled={release.busy} onChange={(e) => setReason(e.target.value)} />
          )}
          <div className="flex flex-wrap gap-2">
            {withdrawing ? (
              <>
                <Button variant="danger" disabled={release.busy} onClick={() => release.withdraw(reason).catch(() => {})}>
                  Withdraw it
                </Button>
                <Button variant="ghost" disabled={release.busy} onClick={() => setWithdrawing(false)}>
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button variant="primary" disabled={release.busy} onClick={() => release.sendAgain().catch(() => {})}>
                  Send again
                </Button>
                <Button variant="danger" disabled={release.busy} onClick={() => setWithdrawing(true)}>
                  Withdraw
                </Button>
              </>
            )}
          </div>
          <ErrorText error={release.error} />
        </div>
      )}
    </>
  );
}
